import React, { useEffect, useState } from "react";
import { Product } from "utils/grocyApi.ts";
import {
  useProductsQuery,
  useQuantityUnitsQuery,
  useUserSettingsQuery,
} from "hooks/useQueries.ts";
import { usePurchaseForm } from "../../hooks/usePurchaseForm.ts";
import { usePurchaseLogic } from "../../hooks/usePurchaseLogic.ts";
import PurchaseModal from "./PurchaseModal.tsx";
import ProductSelectionSection from "./ProductSelectionSection.tsx";
import PurchaseFormFields from "./PurchaseFormFields.tsx";
import PurchaseButton from "./PurchaseButton.tsx";
import ProductImage from "./ProductImage.tsx";
import { ExpiryExtractionResult } from "utils/aiExpiryDate.ts";
import AmountInput from "./AmountInput.tsx";
import { getUnitName } from "utils/quantityUnitHelpers.ts";
import "./PurchaseControls.css";

export interface ManualPurchaseControlsProps {
  onClose: () => void;
  onRequestPhotoCapture?: () => Promise<ExpiryExtractionResult | null>;
}

const ManualPurchaseControls: React.FC<ManualPurchaseControlsProps> = (
  { onClose, onRequestPhotoCapture },
) => {
  const { data: products } = useProductsQuery();
  const { data: quantityUnits = [] } = useQuantityUnitsQuery();
  const { data: userSettings } = useUserSettingsQuery();
  const {
    amount,
    setAmount,
    expiryDate,
    setExpiryDate,
    locationId,
    setLocationId,
    computeExpiryFromDefaultDays,
  } = usePurchaseForm();
  const { handleManualPurchase, isLoading } = usePurchaseLogic({
    onComplete: onClose,
  });

  // Product selection state
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [newProductName, setNewProductName] = useState("");
  const [newProductGroupId, setNewProductGroupId] = useState<number | null>(
    null,
  );

  // Parent product state
  const [parentProductId, setParentProductId] = useState<number | null>(null);
  const [parentProductSearchTerm, setParentProductSearchTerm] = useState<
    string
  >("");

  const handleParentProductSelect = (product: Product) => {
    setParentProductId(product.id);
    setParentProductSearchTerm("");
  };

  const handleParentProductClear = () => {
    setParentProductId(null);
    setParentProductSearchTerm("");
  };

  const handleSelectProduct = (product: Product) => {
    setSelectedProduct(product);
    setSearchTerm(product.name);
    // Auto-fill based on selected product
    setLocationId(product.location_id);
    const parentId = product.parent_product_id ?? null;
    setParentProductId(parentId);
    setParentProductSearchTerm("");
    setExpiryDate(
      computeExpiryFromDefaultDays(product.default_best_before_days),
    );
  };

  const handlePurchase = async () => {
    await handleManualPurchase(
      selectedProduct,
      isCreating,
      newProductName,
      newProductGroupId,
      amount,
      expiryDate,
      locationId,
      parentProductId,
    );
  };

  const presetQuantityUnitId = Number(userSettings?.product_presets_qu_id) ||
    null;
  const purchaseUnitId = selectedProduct?.qu_id_purchase ??
    presetQuantityUnitId ?? undefined;
  const purchaseUnit = quantityUnits.find((unit) => unit.id === purchaseUnitId);
  const purchaseUnitLabel = getUnitName(amount, purchaseUnit);
  const productName = selectedProduct?.name || "Select a product";
  const hasNoOwnStock = !isCreating && selectedProduct?.no_own_stock === 1;

  return (
    <PurchaseModal title={`Purchase: ${productName}`} onClose={onClose}>
      {/* Product Selection */}
      <ProductSelectionSection
        isCreating={isCreating}
        onToggleCreating={setIsCreating}
        searchTerm={searchTerm}
        onSearchTermChange={(term) => {
          setSearchTerm(term);
          if (selectedProduct) {
            setSelectedProduct(null);
          }
        }}
        selectedProduct={selectedProduct}
        onSelectProduct={handleSelectProduct}
        newProductName={newProductName}
        onNewProductNameChange={setNewProductName}
        newProductGroupId={newProductGroupId}
        onNewProductGroupIdChange={setNewProductGroupId}
      />

      {/* Amount Input with Product Image */}
      {(selectedProduct || isCreating) && (
        <div className="purchase-controls__field">
          <div className="purchase-controls__amount-row">
            {selectedProduct
              ? (
                <ProductImage
                  product={selectedProduct}
                  size="medium"
                  className="purchase-controls__product-image"
                />
              )
              : null}
            <AmountInput
              amount={amount}
              onChangeAmount={setAmount}
              unitLabel={purchaseUnitLabel}
            />
          </div>
        </div>
      )}

      {hasNoOwnStock && selectedProduct && (
        <div className="purchase-controls__no-own-stock-warning" role="alert">
          <strong>No own stock</strong>
          <span>
            {selectedProduct.name}{" "}
            is set to not keep its own stock. Choose a child product instead
            before adding stock.
          </span>
        </div>
      )}

      {/* Common Form Fields */}
      <PurchaseFormFields
        expiryDate={expiryDate}
        onExpiryDateChange={setExpiryDate}
        onRequestPhotoCapture={onRequestPhotoCapture}
        locationId={locationId}
        onLocationIdChange={setLocationId}
        showParentSelector
        parentProductId={parentProductId}
        onParentProductSelect={handleParentProductSelect}
        onParentProductClear={handleParentProductClear}
        parentProductSearchTerm={parentProductSearchTerm}
        onParentProductSearchTermChange={setParentProductSearchTerm}
        products={products}
      />

      {/* Purchase Button */}
      <PurchaseButton
        onClick={handlePurchase}
        isLoading={isLoading}
        label={isCreating ? "Create & Purchase" : "Purchase"}
        disabled={(!selectedProduct && !isCreating) ||
          (isCreating && (!newProductName || !locationId)) ||
          !expiryDate ||
          hasNoOwnStock}
      />
    </PurchaseModal>
  );
};

export default ManualPurchaseControls;
